<template>
  <BaseModal
    :model-value="state.open"
    :title="state.title"
    :subtitle="markerLabel"
    :icon="iconName"
    :icon-variant="state.variant"
    size="sm"
    :close-on-overlay="state.mode !== 'confirm'"
    :modal-class="`research-dialog research-dialog--${state.variant}`"
    @update:model-value="onVisibilityChange"
    @close="cancel"
  >
    <div :class="['dialog-sheet', `dialog-sheet--${state.variant}`]">
      <p class="dialog-sheet__message">{{ state.message }}</p>
      <div v-if="state.detail" class="dialog-sheet__detail">
        <span class="dialog-sheet__detail-mark" aria-hidden="true"></span>
        <p>{{ state.detail }}</p>
      </div>
      <dl v-if="state.impact.length" class="impact-ledger">
        <div v-for="item in state.impact" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </div>
      </dl>
    </div>

    <template #footer>
      <BaseButton v-if="state.mode === 'confirm'" ref="cancelButton" variant="outline" @click="cancel">
        {{ state.cancelText }}
      </BaseButton>
      <BaseButton ref="primaryButton" :variant="primaryVariant" :icon="primaryIcon" @click="accept">
        {{ state.confirmText }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import { nextTick } from 'vue';
import BaseModal from './BaseModal.vue';
import BaseButton from './BaseButton.vue';
import { dialogService } from './dialogService';

export default {
  name: 'GlobalDialogHost',
  components: { BaseModal, BaseButton },
  data() { return { state: dialogService.state }; },
  computed: {
    iconName() { return ({ danger: 'AlertTriangle', warning: 'AlertTriangle', success: 'CircleCheck', info: 'Info' })[this.state.variant] || 'Info'; },
    markerLabel() { return this.state.variant === 'danger' ? (this.state.mode === 'confirm' ? '请确认此操作' : '操作未完成') : ({ warning: '请确认此操作', success: '操作已完成', info: '操作提示' })[this.state.variant] || '操作提示'; },
    primaryVariant() { return this.state.variant === 'danger' ? 'danger' : (this.state.variant === 'success' ? 'success' : 'primary'); },
    primaryIcon() { return this.state.mode === 'confirm' ? 'ArrowRight' : 'Check'; },
  },
  watch: {
    'state.open'(open) {
      if (open) nextTick(() => {
        const target = this.state.variant === 'danger' && this.state.mode === 'confirm'
          ? this.$refs.cancelButton
          : this.$refs.primaryButton;
        target?.$el?.focus();
      });
    },
  },
  methods: {
    accept() { dialogService.accept(); },
    cancel() { dialogService.cancel(); },
    onVisibilityChange(open) { if (!open) this.cancel(); },
  },
};
</script>

<style scoped>
:deep(.research-dialog) {
  max-width: 480px;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
}

:deep(.research-dialog .modal__header) {
  position: relative;
  padding: 24px 24px 18px;
  background: var(--surface);
  border-bottom: 0;
}

:deep(.research-dialog .modal__title) {
  color: var(--primary);
  font-family: var(--font-family-serif);
  font-size: 20px;
  line-height: 1.35;
}

:deep(.research-dialog .modal__title-icon) {
  box-sizing: content-box;
  width: 18px;
  height: 18px;
  padding: 9px;
  border-radius: var(--radius-md);
  color: var(--info-dark);
  background: var(--info-surface);
}

:deep(.research-dialog--warning .modal__title-icon) {
  color: var(--warning-dark);
  background: var(--warning-surface);
}

:deep(.research-dialog--danger .modal__title-icon) {
  color: var(--accent-dark);
  background: var(--danger-surface);
}

:deep(.research-dialog--success .modal__title-icon) {
  color: var(--success-dark);
  background: var(--success-surface);
}

:deep(.research-dialog .modal__subtitle) {
  margin-top: 4px;
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.45;
}

:deep(.research-dialog .modal__close) {
  margin-top: 2px;
}

:deep(.research-dialog .modal__body) {
  padding: 0 24px 24px;
}

:deep(.research-dialog .modal__footer) {
  padding: 16px 24px 20px;
  background: var(--surface);
  border-top-color: var(--border-light);
  gap: 10px;
}

:deep(.research-dialog .modal__footer .btn) {
  min-height: 40px;
  padding-inline: 18px;
  border-radius: var(--radius-md);
}

.dialog-sheet {
  padding-left: 14px;
  border-left: 2px solid var(--info);
}

.dialog-sheet--danger { border-left-color: var(--accent); }
.dialog-sheet--warning { border-left-color: var(--warning); }
.dialog-sheet--success { border-left-color: var(--research); }

.dialog-sheet__message {
  margin: 0;
  color: var(--text-strong);
  font-size: 15px;
  font-weight: var(--font-weight-medium);
  line-height: 1.7;
  white-space: pre-line;
}

.dialog-sheet__detail {
  display: grid;
  grid-template-columns: 4px 1fr;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  color: var(--text-muted);
  background: var(--surface-alt);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
}

.dialog-sheet__detail-mark {
  width: 4px;
  min-height: 100%;
  border-radius: var(--radius-full);
  background: var(--border-strong);
}

.dialog-sheet__detail p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-line;
}

.dialog-sheet--danger .dialog-sheet__detail-mark { background: var(--accent); }
.dialog-sheet--warning .dialog-sheet__detail-mark { background: var(--warning); }
.dialog-sheet--success .dialog-sheet__detail-mark { background: var(--research); }
.dialog-sheet--info .dialog-sheet__detail-mark { background: var(--info); }

.impact-ledger {
  margin: 16px 0 0;
  padding: 4px 14px;
  background: var(--surface-alt);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

.impact-ledger div {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-light);
}

.impact-ledger div:last-child { border-bottom: 0; }

.impact-ledger dt { color: var(--text-muted); font-size: 12px; }
.impact-ledger dd {
  margin: 0;
  color: var(--primary);
  font-size: 13px;
  font-weight: var(--font-weight-bold);
  text-align: right;
}

@media (max-width: 560px) {
  :deep(.research-dialog .modal__header) { padding: 20px 20px 16px; }
  :deep(.research-dialog .modal__body) { padding: 0 20px 20px; }
  :deep(.research-dialog .modal__footer) { padding: 14px 20px 18px; }
  :deep(.research-dialog .modal__footer .btn) { flex: 1 1 0; }
}

@media (prefers-reduced-motion: reduce) {
  :deep(.research-dialog) { transition: none !important; }
}
</style>
